// LineChart.js
import React, { useEffect, useState, useCallback } from "react";
import { Line } from "react-chartjs-2";
import {
  getLineChart,
  selectLineChart,
  setTimeRange,
  selectTimeRange,
  selectLoading,
} from "./lineChartSlice";
import { useDispatch, useSelector } from "react-redux";
import { selectFilters } from "../filters/filterSlice";
import {
  CategoryScale,
  LinearScale,
  Chart,
  PointElement,
  LineElement,
} from "chart.js";
import {
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Tooltip,
  TextField,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import * as XLSX from "xlsx";
import { convert_to_request_parameters } from "../../reusable_components/utilityfunctions";

Chart.register(CategoryScale, LinearScale, PointElement, LineElement);

export function LineChart() {
  const { line_chart } = useSelector(selectLineChart);
  const timeRange = useSelector(selectTimeRange);
  const loading = useSelector(selectLoading);
  const filters = useSelector(selectFilters);
  const dispatch = useDispatch();

  const [selectedRange, setSelectedRange] = useState(timeRange || "3 months");
  const [isCustomDateOpen, setIsCustomDateOpen] = useState(false);
  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null);

  const getDateRange = useCallback(
    (range) => {
      const endDate = new Date();
      let startDate = new Date();

      switch (range) {
        case "1 week":
          startDate.setDate(endDate.getDate() - 7);
          break;
        case "1 month":
          startDate.setMonth(endDate.getMonth() - 1);
          break;
        case "3 months":
          startDate.setMonth(endDate.getMonth() - 3);
          break;
        case "6 months":
          startDate.setMonth(endDate.getMonth() - 6);
          break;
        case "1 year":
          startDate.setFullYear(endDate.getFullYear() - 1);
          break;
        default:
          // NOTE: If range is "custom", we don't use this function's logic.
          // We handle custom in the useEffect directly.
          return { startDate: null, endDate: null };
      }
      return { startDate, endDate };
    },
    [], // Removed customStartDate/EndDate dependency here as it's handled directly now
  );

  // Fetch data when time range or filters change
  useEffect(() => {
    // 1. Handle Custom Range logic FIRST
    if (selectedRange === "custom") {
      if (customStartDate && customEndDate) {
        // Use the valid custom dates
        const result = convert_to_request_parameters(
          { startDate: customStartDate, endDate: customEndDate, key: "range" },
          filters.startTime,
          filters.endTime,
        );
        dispatch(getLineChart(result.start, result.end));
      } else {
        // If custom is selected but no dates are chosen yet, fallback to 3 months
        const { startDate, endDate } = getDateRange("3 months");
        const result = convert_to_request_parameters(
          { startDate, endDate, key: "range" },
          filters.startTime,
          filters.endTime,
        );
        dispatch(getLineChart(result.start, result.end));
      }
      return; // Stop execution so we don't run the standard range code
    }

    // 2. Handle Standard Ranges (1 week, 1 month, etc)
    const { startDate, endDate } = getDateRange(selectedRange);
    const result = convert_to_request_parameters(
      { startDate, endDate, key: "range" },
      filters.startTime,
      filters.endTime,
    );
    dispatch(getLineChart(result.start, result.end));
  }, [
    selectedRange,
    customStartDate,
    customEndDate,
    filters,
    dispatch,
    getDateRange,
  ]);

  const handleTimeRangeChange = (event, newRange) => {
    if (newRange !== null) {
      if (newRange === "custom") {
        setIsCustomDateOpen(true);
        setSelectedRange("custom");
        dispatch(setTimeRange("custom"));
      } else {
        setSelectedRange(newRange);
        dispatch(setTimeRange(newRange));
        // Clear custom dates when switching to a standard range
        setCustomStartDate(null);
        setCustomEndDate(null);
      }
    }
  };

  const handleCustomDateApply = () => {
    if (customStartDate && customEndDate) {
      // 1. Close the dialog
      setIsCustomDateOpen(false);

      // 2. Ensure the mode is set to custom
      setSelectedRange("custom");
      dispatch(setTimeRange("custom"));

      // NOTE: The useEffect will automatically trigger
      // because customStartDate and customEndDate are already set!
    } else {
      alert("Please select both a Start Date and an End Date.");
    }
  };

  const handleCustomDateClear = () => {
    setCustomStartDate(null);
    setCustomEndDate(null);
    setSelectedRange("3 months");
    dispatch(setTimeRange("3 months"));
    setIsCustomDateOpen(false);
  };

  const handleDownloadExcel = () => {
    if (!line_chart || !line_chart.labels || line_chart.labels.length === 0) {
      alert("No data available to download.");
      return;
    }

    const excelData = [];
    const header = ["Date"];
    line_chart.datasets.forEach((dataset) => {
      header.push(dataset.label);
    });
    excelData.push(header);

    line_chart.labels.forEach((label, index) => {
      const row = [label];
      line_chart.datasets.forEach((dataset) => {
        row.push(dataset.data[index] || 0);
      });
      excelData.push(row);
    });

    const ws = XLSX.utils.aoa_to_sheet(excelData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Detection Trends");

    const range = XLSX.utils.decode_range(ws["!ref"]);
    ws["!cols"] = [];
    for (let C = range.s.c; C <= range.e.c; C++) {
      ws["!cols"].push({ wch: 20 });
    }

    const fileName = `Detection_Trends_${new Date().toISOString().split("T")[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  const timeRanges = [
    { value: "1 week", label: "1 Week" },
    { value: "1 month", label: "1 Month" },
    { value: "3 months", label: "3 Months" },
    { value: "6 months", label: "6 Months" },
    { value: "1 year", label: "1 Year" },
  ];

  return (
    <Box sx={{ width: "100%", height: "100%" }}>
      {/* TOP SECTION: Title on Left, Controls on Right, taking full width */}
      <Box
        sx={{
          mb: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          flexWrap: "wrap",
          gap: 2,
          width: "100%",
        }}
      >
        {/* LEFT: Detection Trends Title */}
        <Typography
          variant="h6"
          sx={{
            color: "#454545",
            fontWeight: 600,
            whiteSpace: "nowrap",
            mt: 1, // Align visually with the buttons
          }}
        >
          Detection Trends
        </Typography>

        {/* RIGHT: All Controls grouped together */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
            gap: 1,
          }}
        >
          {/* Row 1: Time Range Label + Custom Range + Download Excel */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontWeight: 500 }}
            >
              Time Range:
            </Typography>

            <Button
              variant={selectedRange === "custom" ? "contained" : "outlined"}
              size="small"
              onClick={() => setIsCustomDateOpen(true)}
              sx={{
                borderRadius: "20px",
                textTransform: "none",
                fontSize: "0.75rem",
                py: 0.5,
                px: 1.5,
                borderColor: "divider",
                backgroundColor:
                  selectedRange === "custom" ? "#454545" : "transparent",
                color: selectedRange === "custom" ? "white" : "inherit",
                "&:hover": {
                  backgroundColor:
                    selectedRange === "custom"
                      ? "#5a5a5a"
                      : "rgba(69,69,69,0.08)",
                },
              }}
            >
              Custom Range
            </Button>

            <Tooltip title="Download as Excel (.xlsx)">
              <Button
                variant="outlined"
                size="small"
                startIcon={<FileDownloadIcon />}
                onClick={handleDownloadExcel}
                disabled={
                  loading || !line_chart || line_chart.labels.length === 0
                }
                sx={{
                  borderRadius: "20px",
                  textTransform: "none",
                  borderColor: "#454545",
                  color: "#454545",
                  "&:hover": {
                    borderColor: "#454545",
                    backgroundColor: "rgba(69,69,69,0.05)",
                  },
                }}
              >
                Download Excel
              </Button>
            </Tooltip>
          </Box>

          {/* Row 2: Standard Time Range Toggle Buttons (Aligned to right) */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              justifyContent: "flex-end",
            }}
          >
            <ToggleButtonGroup
              value={selectedRange}
              exclusive
              onChange={handleTimeRangeChange}
              size="small"
              sx={{
                "& .MuiToggleButton-root": {
                  textTransform: "none",
                  fontSize: "0.75rem",
                  py: 0.5,
                  px: 1.5,
                  borderRadius: "20px !important",
                  border: "1px solid",
                  borderColor: "divider",
                  "&.Mui-selected": {
                    backgroundColor: "#454545",
                    color: "white",
                    borderColor: "#454545",
                    "&:hover": {
                      backgroundColor: "#5a5a5a",
                    },
                  },
                  "&:hover": {
                    backgroundColor: "rgba(69, 69, 69, 0.08)",
                  },
                },
              }}
            >
              {timeRanges.map((range) => (
                <ToggleButton key={range.value} value={range.value}>
                  {range.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>
        </Box>
      </Box>

      {/* Custom Date Range Dialog */}
      <Dialog
        open={isCustomDateOpen}
        onClose={() => setIsCustomDateOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Select Custom Date Range</DialogTitle>
        <DialogContent>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Stack spacing={2} sx={{ mt: 2 }}>
              <DatePicker
                label="Start Date"
                value={customStartDate}
                onChange={(newValue) => setCustomStartDate(newValue)}
                renderInput={(params) => (
                  <TextField {...params} fullWidth size="small" />
                )}
              />
              <DatePicker
                label="End Date"
                value={customEndDate}
                onChange={(newValue) => setCustomEndDate(newValue)}
                renderInput={(params) => (
                  <TextField {...params} fullWidth size="small" />
                )}
              />
            </Stack>
          </LocalizationProvider>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCustomDateClear}>Clear & Reset</Button>
          <Button
            onClick={handleCustomDateApply}
            variant="contained"
            disabled={!customStartDate || !customEndDate}
          >
            Apply Dates
          </Button>
        </DialogActions>
      </Dialog>

      {/* CHART SECTION: Full Width Chart */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "300px",
          width: "100%",
          position: "relative",
        }}
      >
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
              width: "100%",
            }}
          >
            <CircularProgress size={40} />
          </Box>
        ) : line_chart && line_chart.labels && line_chart.labels.length > 0 ? (
          <Line
            height={300}
            data={line_chart}
            type="line"
            options={{
              responsive: true,
              maintainAspectRatio: false,
              interaction: {
                mode: "index",
                intersect: false,
              },
              stacked: false,
              scales: {
                y: {
                  type: "linear",
                  display: true,
                  position: "left",
                  beginAtZero: true,
                  grid: {
                    drawOnChartArea: true,
                  },
                },
                y1: {
                  type: "linear",
                  display: true,
                  position: "right",
                  beginAtZero: true,
                  grid: {
                    drawOnChartArea: false,
                  },
                },
                x: {
                  grid: {
                    display: false,
                  },
                },
              },
              plugins: {
                legend: {
                  position: "top",
                  labels: {
                    boxWidth: 12,
                    padding: 15,
                    font: {
                      size: 11,
                    },
                  },
                },
                tooltip: {
                  mode: "index",
                  intersect: false,
                },
              },
            }}
          />
        ) : (
          <Box
            sx={{ textAlign: "center", color: "text.secondary", width: "100%" }}
          >
            <Typography variant="body2">
              No data available for the selected range
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}
